import { api } from '../client';

// ==========================================
// Types
// ==========================================

export type ForecastMethod =
  | 'MOVING_AVERAGE'
  | 'EXPONENTIAL_SMOOTHING'
  | 'LINEAR_REGRESSION'
  | 'SEASONAL'
  | 'MACHINE_LEARNING';
export type ForecastStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export interface ForecastPeriod {
  id: string;
  forecastId: string;
  periodStart: string;
  periodEnd: string;
  predictedDemand: number;
  actualDemand: number | null;
  lowerBound: number | null;
  upperBound: number | null;
  variance: number | null;
  createdAt: string;
}

export interface DemandForecast {
  id: string;
  companyId: string;
  itemId: string;
  item: {
    id: string;
    name: string;
    sku: string;
    category: string;
  };
  locationId: string | null;
  location: {
    id: string;
    name: string;
    code: string;
  } | null;
  forecastMethod: ForecastMethod;
  forecastPeriod: string;
  startDate: string;
  endDate: string;
  status: ForecastStatus;
  confidence: number | null;
  mape: number | null;
  generatedById: string | null;
  notes: string | null;
  periods: ForecastPeriod[];
  _count?: {
    periods: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface HistoricalDemand {
  id: string;
  companyId: string;
  itemId: string;
  item: {
    id: string;
    name: string;
    sku: string;
  };
  locationId: string | null;
  location: {
    id: string;
    name: string;
  } | null;
  periodDate: string;
  quantity: number;
  unitCost: number | null;
  equipmentId: string | null;
  source: string;
  sourceId: string | null;
  createdAt: string;
}

export interface ForecastAccuracy {
  id: string;
  companyId: string;
  itemId: string;
  periodMonth: string;
  forecastedQty: number;
  actualQty: number;
  absoluteError: number;
  percentageError: number;
  createdAt: string;
}

export interface ForecastDashboard {
  kpis: {
    totalForecasts: number;
    activeForecasts: number;
    draftForecasts: number;
    averageMape: number | null;
    accuracyTrend: number | null;
  };
  recentForecasts: DemandForecast[];
  accuracyHistory: {
    month: string;
    mape: number;
  }[];
}

// ==========================================
// DTOs
// ==========================================

export interface CreateForecastPeriodDto {
  periodStart: string;
  periodEnd: string;
  predictedDemand: number;
  lowerBound?: number;
  upperBound?: number;
}

export interface CreateForecastDto {
  itemId: string;
  locationId?: string;
  forecastMethod?: ForecastMethod;
  forecastPeriod: string;
  startDate: string;
  endDate: string;
  status?: ForecastStatus;
  confidence?: number;
  notes?: string;
  periods?: CreateForecastPeriodDto[];
}

export interface UpdateForecastDto {
  forecastMethod?: ForecastMethod;
  startDate?: string;
  endDate?: string;
  status?: ForecastStatus;
  confidence?: number;
  mape?: number;
  notes?: string;
}

export interface QueryForecastParams {
  itemId?: string;
  locationId?: string;
  forecastMethod?: ForecastMethod;
  status?: ForecastStatus;
  forecastPeriod?: string;
  startDateFrom?: string;
  startDateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface GenerateForecastDto {
  itemId: string;
  locationId?: string;
  forecastMethod?: ForecastMethod;
  forecastPeriod: string;
  periodsToForecast?: number;
  lookbackPeriods?: number;
  smoothingFactor?: number;
  startDate?: string;
  notes?: string;
}

export interface SyncHistoricalDto {
  itemId: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
}

export interface QueryHistoricalDemandParams {
  locationId?: string;
  startDate?: string;
  endDate?: string;
  source?: string;
  limit?: number;
}

export interface QueryAccuracyParams {
  itemId?: string;
  startMonth?: string;
  endMonth?: string;
  limit?: number;
}

export interface CalculateAccuracyDto {
  itemId?: string;
  month?: string;
  monthsBack?: number;
}

// ==========================================
// Response Types
// ==========================================

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface AccuracyMetricsResponse {
  records: ForecastAccuracy[];
  summary: {
    totalRecords: number;
    averageMape: number | null;
    bestMape: number | null;
    worstMape: number | null;
  };
}

interface SyncHistoricalResponse {
  success: boolean;
  recordsSynced: number;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

interface CalculateAccuracyResponse {
  success: boolean;
  periodsCalculated: number;
  results: {
    forecastId: string;
    periodStart: string;
    forecastedQty: number;
    actualQty: number;
    percentageError: number;
  }[];
}

// ==========================================
// API Functions
// ==========================================

export const forecastingApi = {
  // Dashboard
  getDashboard: () => api.get<ForecastDashboard>('/forecasting/dashboard'),

  // Forecast CRUD
  getAll: (params?: QueryForecastParams) =>
    api.get<PaginatedResponse<DemandForecast>>('/forecasting', { params }),

  getById: (id: string) => api.get<DemandForecast>(`/forecasting/${id}`),

  create: (data: CreateForecastDto) => api.post<DemandForecast>('/forecasting', data),

  update: (id: string, data: UpdateForecastDto) =>
    api.patch<DemandForecast>(`/forecasting/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean; message: string }>(`/forecasting/${id}`),

  // Auto-Generate Forecast
  generate: (data: GenerateForecastDto) => api.post<DemandForecast>('/forecasting/generate', data),

  // Historical Demand
  getHistoricalDemand: (itemId: string, params?: QueryHistoricalDemandParams) =>
    api.get<HistoricalDemand[]>(`/forecasting/historical/${itemId}`, { params }),

  syncHistorical: (data: SyncHistoricalDto) =>
    api.post<SyncHistoricalResponse>('/forecasting/historical/sync', data),

  // Accuracy Tracking
  getAccuracy: (params?: QueryAccuracyParams) =>
    api.get<AccuracyMetricsResponse>('/forecasting/accuracy', { params }),

  calculateAccuracy: (data: CalculateAccuracyDto) =>
    api.post<CalculateAccuracyResponse>('/forecasting/accuracy/calculate', data),
};
